<template>
  <operation-help-dialog
    :model-value="modelValue"
    :document="helpDocument"
    @update:model-value="$emit('update:modelValue', $event)"
  />
</template>

<script>
import { OperationHelpDialog } from "@/components/operation-help";

const commonReminders = [
  "原出库申请在库管审核出库前，可由申请创建人或库存管理人员填写原因后取消。",
  "取消单条出库明细时必须至少保留一条有效明细；如需全部取消，请使用“取消申请”。"
];

const repairDocument = {
  name: "维修出库待回货",
  summary: "用于将库存货品送出维修，并在维修完成后登记回货、完成成本复核和后续入库。",
  flow: [
    "新增维修出库申请",
    "部门负责人审核",
    "成本复核",
    "库管审核出库",
    "自动生成待回货单",
    "分批录入维修回货",
    "每批负责人审核",
    "每批成本复核",
    "每批库管审核",
    "普通入库审核"
  ],
  steps: [
    {
      title: "填写申请信息",
      content: "选择所属公司和单据日期，填写申请人及备注；维修流程固定采用回货入库结案。"
    },
    {
      title: "添加出库货品",
      content: "点击“添加出库货品”，可按批次号、系统款号或品牌款号查询库存；选择货品后，确认出库数量大于 0 且不超过可用数。"
    },
    {
      title: "提交并完成出库审核",
      content: "提交出库申请后，依次由部门负责人审核、成本复核和库管审核出库。库管审核后才会正式扣减库存，并自动生成以 -R 结尾的待回货单。"
    },
    {
      title: "打开待回货单",
      content: "出库完成后点击“打开待回货单”。系统已按正式出库流水生成预计维修回货明细，可查看预计、审核中、已完成和待回数量。"
    },
    {
      title: "录入并核对回货明细",
      content: "从预计维修明细加入本批，可拆分数量并调整仓库、渠道、批次号、重量、成本及计划售价；实际系统款式必须与预计明细一致。"
    },
    {
      title: "逐批完成回货审核",
      content: "每个回货批次独立完成负责人审核、成本复核和库管审核，并生成一张独立的维修入库待审核单；所有预计明细回足后父回货单自动完成。"
    },
    {
      title: "完成普通入库",
      content: "每个批次生成的维修入库单仍需完成普通入库审核，审核通过后才形成正式入库库存。"
    }
  ],
  reminders: [
    "库管审核出库前不会扣减来源库存；完成待回货也不会直接形成正式入库库存。",
    "同一预计明细可跨多个批次归还，但处理中与已完成数量合计不能超过预计数量。",
    ...commonReminders
  ]
};

const tollingDocument = {
  name: "送料加工待回货",
  summary: "用于登记半成品或其他库存的送料加工出库、计划成品和多批次实际回货，并统一完成差异处理及入库。",
  flow: [
    "新增送料加工申请",
    "部门负责人审核",
    "成本复核",
    "库管审核出库",
    "自动生成待回货单",
    "分批录入回货",
    "提交全部回货审核",
    "待回货确认",
    "成本复核",
    "完成待回货",
    "普通入库审核"
  ],
  steps: [
    {
      title: "填写申请与加工信息",
      content: "填写所属公司、单据日期、申请人和备注，并选择加工供应商，维护加工单号、联系人、联系电话及预计回货日期。加工供应商和预计回货日期为必填信息。"
    },
    {
      title: "添加送料货品",
      content: "点击“添加出库货品”选择要送加工的库存。系统默认从半成品库选择；切换到其他仓库需要库存管理权限。"
    },
    {
      title: "维护计划回货成品",
      content: "新增计划成品并选择系统款号，填写默认入库仓库和计划数量。同一计划成品请合并数量，不要重复添加。"
    },
    {
      title: "提交并完成出库审核",
      content: "提交申请后，依次完成部门负责人审核、成本复核和库管审核出库。库管审核后扣减送料库存，并自动生成送料加工待回货单。"
    },
    {
      title: "逐批登记实际回货",
      content: "在待回货单中填写回货日期、收货人、送货单号及批次备注；批次编号留空时由系统自动生成。回货日期和收货人为必填信息。"
    },
    {
      title: "维护并保存批次明细",
      content: "为当前批次添加实际回货明细，可关联计划成品，也可登记非计划成品。保存批次后，在提交全部回货审核前仍可编辑或删除已保存批次。"
    },
    {
      title: "提交全部回货审核",
      content: "所有批次录入完成，并确认当前没有尚未保存的回货明细后，点击“提交全部回货审核”。"
    },
    {
      title: "处理回货差异",
      content: "实际回货与计划不一致时，必须填写差异原因、责任方和差异说明后执行差异结案。差异结案只记录差异，不会回冲已送料库存，也不会自动生成损耗单。"
    },
    {
      title: "完成审核和普通入库",
      content: "依次完成待回货确认、成本复核和库管处理后，系统将全部批次合并生成一张普通入库待审核单；完成普通入库审核后才形成正式库存。"
    }
  ],
  reminders: [
    "每次保存只保存当前回货批次；全部批次确认无误后，还必须点击“提交全部回货审核”。",
    "计划数量与实际回货不一致时不能直接提交，必须完成差异结案信息。",
    "普通入库单审核完成前，送料加工回货不会进入正式库存。",
    ...commonReminders
  ]
};

function goldDocument(completionMode) {
  const isReturn = completionMode === "return";
  const completionName = isReturn ? "回货入库" : "回款结案";
  return {
    name: `融金出库待回货（${completionName}）`,
    summary: isReturn
      ? "当前采用回货入库结案：融金货品出库后，需要登记实际回货明细，并继续完成普通入库审核。"
      : "当前采用回款结案：融金货品出库后，通过登记回款记录完成结案，不会产生入库流水。",
    flow: isReturn
      ? ["选择回货入库", "新增融金出库申请", "部门负责人审核", "财务复核", "库管审核出库", "自动生成待回货单", "录入回货", "待回货确认", "财务复核", "完成待回货", "普通入库审核"]
      : ["选择回款结案", "新增融金出库申请", "部门负责人审核", "财务复核", "库管审核出库", "自动生成待回货单", "录入回款", "待回货确认", "财务复核", "完成结案"],
    steps: [
      {
        title: "选择结案方式",
        content: `新增时选择“回货入库”或“回款结案”，提交后不能修改。融金流程默认采用回款结案；当前帮助内容对应“${completionName}”。`
      },
      {
        title: "填写申请并添加出库货品",
        content: "填写所属公司、单据日期、申请人和备注，添加需要融金出库的货品，并确认出库数量大于 0 且不超过库存可用数。"
      },
      {
        title: "提交并完成出库审核",
        content: "提交申请后，依次完成部门负责人审核、财务复核和库管审核出库。库管审核后正式扣减库存，并自动生成融金待回货单。"
      },
      isReturn
        ? {
            title: "录入回货入库明细",
            content: "在待回货单中手工新增、按原出库货品快速添加或批量导入回货明细，并完整核对仓库、渠道、批次、系统款号、计价方式、数量、重量、成本和计划售价。"
          }
        : {
            title: "录入回款记录",
            content: "点击“新增回款记录”，填写关联批次号、库房、渠道、融金克重、融金金重、回款金额和备注。回款记录只用于结案，不会生成入库流水。"
          },
      {
        title: "完成待回货审核",
        content: "保存回货明细或回款记录后，依次执行“待回货确认”、财务复核和“完成待回货”。"
      },
      isReturn
        ? {
            title: "完成普通入库",
            content: "完成待回货后会生成普通入库待审核单；完成普通入库审核后，回货才进入正式库存。"
          }
        : {
            title: "确认结案结果",
            content: "回款结案完成后只保留融金出库流水和回款记录，不会生成普通入库单或正式入库库存。"
          }
    ],
    reminders: [
      isReturn
        ? "回货入库结案会生成普通入库待审核单，但完成待回货不等于已经正式入库。"
        : "回款结案不会生成任何入库流水，请在提交前确认结案方式和回款金额。",
      "融金单据提交后不能切换结案方式；如选择错误，应在库管审核出库前取消申请并重新提交。",
      ...commonReminders
    ]
  };
}

export default {
  name: "PendingReturnHelpDialog",
  components: { OperationHelpDialog },
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    template: {
      type: String,
      default: "repair"
    },
    completionMode: {
      type: String,
      default: "return"
    }
  },
  emits: ["update:modelValue"],
  computed: {
    helpDocument() {
      if (this.template === "gold") return goldDocument(this.completionMode);
      if (this.template === "tolling") return tollingDocument;
      return repairDocument;
    }
  }
};
</script>

<style scoped>
.pending-help-body {
  max-height: 68vh;
  overflow-y: auto;
  padding: 0 4px 8px 0;
}

.help-summary {
  margin-bottom: 22px;
}

.help-section + .help-section {
  margin-top: 24px;
}

.help-section-title {
  margin: 0 0 14px;
  color: var(--el-text-color-primary);
  font-size: 16px;
}

.process-flow {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  padding: 14px;
  border: 1px solid var(--el-border-color-lighter);
  border-radius: 6px;
  background: var(--el-fill-color-lighter);
}

.process-node {
  padding: 7px 11px;
  border: 1px solid var(--el-color-primary-light-5);
  border-radius: 16px;
  background: var(--el-color-primary-light-9);
  color: var(--el-color-primary-dark-2);
  font-size: 13px;
  line-height: 18px;
}

.process-arrow {
  flex: 0 0 auto;
  color: var(--el-text-color-secondary);
}

.operation-timeline {
  padding-left: 4px;
}

.step-card {
  padding: 12px 14px;
  border: 1px solid var(--el-border-color-lighter);
  border-radius: 6px;
  background: var(--el-bg-color-page);
}

.step-card h4 {
  margin: 0 0 6px;
  color: var(--el-text-color-primary);
  font-size: 14px;
}

.step-card p {
  margin: 0;
  color: var(--el-text-color-regular);
  line-height: 1.7;
}

.help-reminders {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.help-reminders .help-section-title {
  margin-bottom: 4px;
}

@media (max-width: 600px) {
  .pending-help-body {
    max-height: 72vh;
  }

  .process-flow {
    align-items: stretch;
    flex-direction: column;
  }

  .process-node {
    border-radius: 4px;
    text-align: center;
  }

  .process-arrow {
    align-self: center;
    transform: rotate(90deg);
  }
}
</style>
